import React from "react";
import {
  Card,
  Result,
  Segmented,
  Spin,
  Button,
  Space,
  Typography,
  Tooltip,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

// Hook (Controller)
import { useInstitutionDashboardController } from "./useInstitutionDashboardController";

// Componentes Visuales Reutilizados
// Ajusta la ruta relativa según tu estructura real
import {
  KpiCards,
  ChartsRow,
  RecentActivityTable,
} from "../../../../components/Admin/TherapistCmp/DashboardCmp";

// ✅ BARRA DE SELECCIÓN VISUAL
import { TherapistSelectorBar } from "./TherapistSelectorBar";

// Estilos locales
import "./Page_Institution_Dashboard.scss";
import { useMovil } from "../../../../hooks/useIsNarrow";

const { Title, Text } = Typography;

// El valor que llega del backend se queda como esta (es una clave tecnica); lo
// que se traduce es la etiqueta que lo acompana en pantalla.
const TIME_RANGE_LABEL_KEY = {
  last_7_days: "dashboard.range.last7Long",
  last_30_days: "dashboard.range.last30Long",
  last_90_days: "dashboard.range.last90Long",
  all_time: "dashboard.range.allLong",
};

export function Page_Institution_Dashboard() {
  const movil = useMovil();
  const { t } = useTranslation(["institution", "common"]);

  const {
    timeRange,
    setTimeRange,
    selectedTherapist,
    setSelectedTherapy,
    therapistsList,
    summary,
    charts,
    activity,
    loading,
    error,
    reload,
  } = useInstitutionDashboardController();

  // Renderizado del Header
  const renderHeader = () => (
    <div className="revi-page-inst-dashboard__header">
      <div className="revi-page-inst-dashboard__title">
        <Title level={2}>{t("dashboard.title")}</Title>
        <Text type="secondary" className="revi-subtitulo-pagina">
          {t("dashboard.subtitle")}
        </Text>
      </div>

      <div className="revi-page-inst-dashboard__actions">
        {/* FILTRO TIEMPO Y RECARGA */}
        <Space>
          <Segmented
            block={movil}
            value={timeRange}
            onChange={setTimeRange}
            options={[
              { label: t("dashboard.range.last7"), value: "last_7_days" },
              { label: t("dashboard.range.last30"), value: "last_30_days" },
              { label: t("dashboard.range.last90"), value: "last_90_days" },
              { label: t("dashboard.range.all"), value: "all_time" },
            ]}
          />
          <Tooltip title={t("dashboard.reload")}>
            <Button
              icon={<ReloadOutlined />}
              onClick={reload}
              loading={loading && !summary}
            />
          </Tooltip>
        </Space>
      </div>
    </div>
  );

  return (
    <div className="revi-page-inst-dashboard">
      <div className="revi-page-inst-dashboard__main">
        {renderHeader()}

        {/* BARRA DE SELECCIÓN VISUAL */}
        <TherapistSelectorBar
          therapists={therapistsList}
          selectedId={selectedTherapist}
          onSelect={setSelectedTherapy}
          loading={loading}
        />

        {/* ESTADO DE ERROR */}
        {error ? (
          <Card variant="outlined" className="revi-page-inst-dashboard__error">
            <Result
              status="error"
              title={t("dashboard.errorTitle")}
              subTitle={error}
              extra={
                <Button type="primary" onClick={reload}>
                  {t("common:actions.retry")}
                </Button>
              }
            />
          </Card>
        ) : (
          <>
            {/* 1. KPIs */}
            <div className="revi-page-inst-dashboard__section">
              <KpiCards
                loading={loading}
                summary={summary}
                timeRangeLabel={t(TIME_RANGE_LABEL_KEY[timeRange])}
              />
            </div>

            {/* 2. GRÁFICAS */}
            <div className="revi-page-inst-dashboard__section">
              <ChartsRow loading={loading} charts={charts} />
            </div>

            {/* 3. ACTIVIDAD RECIENTE */}
            <div className="revi-page-inst-dashboard__section">
              <RecentActivityTable loading={loading} activity={activity} />
            </div>

            {/* OVERLAY DE CARGA (CORREGIDO PARA EVITAR WARNING) */}
            {loading && summary && (
              <div className="revi-page-inst-dashboard__overlay">
                <div style={{ textAlign: "center" }}>
                  <Spin size="large" />
                  <div
                    style={{
                      marginTop: 16,
                      color: "#1677ff",
                      fontWeight: 500,
                    }}
                  >
                    {t("dashboard.updating")}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
